import type { AgentId, CardPushItem, TriageCardRow } from '@voll-intelligence/domain';
import type {
  ConversationBoardResponse,
  TaskBoardResponse,
  TaskWithCard,
} from '@web/data/repositories';

type Columns = ConversationBoardResponse['columns'];
const COLUMNS = ['backlog', 'in_progress', 'resolved'] as const;

/**
 * Applies a coalesced batch of conversation-board deltas to a board snapshot,
 * returning a new snapshot (pure — never mutates the input). Non-conversation
 * items (task/triage) are ignored. Upsert drops any stale copy first, then
 * places the card at the front of its derived column; remove drops it.
 */
export function applyConversationItems(
  board: ConversationBoardResponse,
  items: CardPushItem[],
): ConversationBoardResponse {
  const columns: Columns = {
    backlog: [...board.columns.backlog],
    in_progress: [...board.columns.in_progress],
    resolved: [...board.columns.resolved],
  };

  const drop = (id: string): void => {
    for (const col of COLUMNS) {
      const i = columns[col].findIndex((c) => c.id === id);
      if (i >= 0) columns[col].splice(i, 1);
    }
  };

  for (const item of items) {
    if (item.kind === 'conversation_upsert') {
      drop(item.card.id);
      columns[item.column].unshift(item.card);
    } else if (item.kind === 'conversation_remove') {
      drop(item.cardId);
    }
  }

  return { columns };
}

type TaskColumns = TaskBoardResponse['columns'];
const TASK_COLUMNS = ['ai_handling', 'awaiting_human', 'human_handling', 'resolved'] as const;

/** The viewer's own active task lives in the "my tasks" hero, not the columns. */
const isOwnActive = (task: TaskWithCard, myAgentId: AgentId | null): boolean =>
  myAgentId != null && task.assigneeAgent?.id === myAgentId && task.status !== 'done';

/**
 * Applies a coalesced batch of task deltas to the task board columns. Skips the
 * viewer's own active tasks (those go to {@link applyMyTasks}). Pure.
 */
export function applyTaskItems(
  board: TaskBoardResponse,
  items: CardPushItem[],
  myAgentId: AgentId | null,
): TaskBoardResponse {
  const columns: TaskColumns = {
    ai_handling: [...board.columns.ai_handling],
    awaiting_human: [...board.columns.awaiting_human],
    human_handling: [...board.columns.human_handling],
    resolved: [...board.columns.resolved],
  };
  const drop = (id: string): void => {
    for (const col of TASK_COLUMNS) {
      const i = columns[col].findIndex((t) => t.id === id);
      if (i >= 0) columns[col].splice(i, 1);
    }
  };
  for (const item of items) {
    if (item.kind === 'task_upsert') {
      drop(item.task.id);
      if (!isOwnActive(item.task, myAgentId)) columns[item.column].unshift(item.task);
    } else if (item.kind === 'task_remove') {
      drop(item.taskId);
    }
  }
  return { columns };
}

/**
 * Applies a coalesced batch of task deltas to the flat "my tasks" list (the
 * viewer's own active tasks). Unchanged when the viewer has no agent. Pure.
 */
export function applyMyTasks(
  list: readonly TaskWithCard[],
  items: CardPushItem[],
  myAgentId: AgentId | null,
): TaskWithCard[] {
  const next = [...list];
  if (myAgentId == null) return next;
  const drop = (id: string): void => {
    const i = next.findIndex((t) => t.id === id);
    if (i >= 0) next.splice(i, 1);
  };
  for (const item of items) {
    if (item.kind === 'task_upsert') {
      drop(item.task.id);
      if (isOwnActive(item.task, myAgentId)) next.unshift(item.task);
    } else if (item.kind === 'task_remove') {
      drop(item.taskId);
    }
  }
  return next;
}

/**
 * Evicts cards that now live in another squad. A card belongs to exactly one
 * squad, so a `conversation_upsert` naming squad B implies the card must leave
 * every other squad's board — the watcher can't emit that remove (no pre-image
 * of the old squad), so the client enforces the invariant. Returns the SAME
 * reference when nothing was dropped (no needless rerender). Pure.
 */
export function dropConversationStrays(
  board: ConversationBoardResponse,
  strayCardIds: ReadonlySet<string>,
): ConversationBoardResponse {
  if (strayCardIds.size === 0) return board;
  const columns: Columns = { ...board.columns };
  let changed = false;
  for (const col of COLUMNS) {
    const kept = columns[col].filter((c) => !strayCardIds.has(c.id));
    if (kept.length !== columns[col].length) {
      columns[col] = kept;
      changed = true;
    }
  }
  return changed ? { columns } : board;
}

/** Task-board analogue of {@link dropConversationStrays}, keyed by task id. */
export function dropTaskStrays(
  board: TaskBoardResponse,
  strayTaskIds: ReadonlySet<string>,
): TaskBoardResponse {
  if (strayTaskIds.size === 0) return board;
  const columns: TaskColumns = { ...board.columns };
  let changed = false;
  for (const col of TASK_COLUMNS) {
    const kept = columns[col].filter((t) => !strayTaskIds.has(t.id));
    if (kept.length !== columns[col].length) {
      columns[col] = kept;
      changed = true;
    }
  }
  return changed ? { columns } : board;
}

/** "My tasks" analogue — drops the viewer's own tasks that moved to another squad. */
export function dropMyTaskStrays(
  list: readonly TaskWithCard[],
  strayTaskIds: ReadonlySet<string>,
): TaskWithCard[] {
  if (strayTaskIds.size === 0) return list as TaskWithCard[];
  const kept = list.filter((t) => !strayTaskIds.has(t.id));
  return kept.length === list.length ? (list as TaskWithCard[]) : kept;
}

/** Mirrors the server list cap (`OVERVIEW_ROW_CAP` in the kanban api's
 *  `aggregateTriageCards`) so live pushes can't grow the cached inbox beyond
 *  what a fresh fetch would return. */
const TRIAGE_ROW_CAP = 50;

const TRIAGE_PRIORITY_RANK: Record<TriageCardRow['priority'], number> = {
  urgent: 4,
  high: 3,
  normal: 2,
  low: 1,
};

/** VIP customers and urgent cards form the top tier of the queue (rank 0). */
const triageTopRank = (r: TriageCardRow): number =>
  r.customer.vip || r.priority === 'urgent' ? 0 : 1;

/** Mirrors the server sort in `aggregateTriageCards`: VIP/urgent tier first,
 *  then priority desc, then newest first, card id desc as tiebreaker. */
function compareTriageRows(a: TriageCardRow, b: TriageCardRow): number {
  const tier = triageTopRank(a) - triageTopRank(b);
  if (tier !== 0) return tier;
  const priority = TRIAGE_PRIORITY_RANK[b.priority] - TRIAGE_PRIORITY_RANK[a.priority];
  if (priority !== 0) return priority;
  const created = b.createdAt.getTime() - a.createdAt.getTime();
  if (created !== 0) return created;
  return b.cardId.localeCompare(a.cardId);
}

/**
 * Applies a coalesced batch of triage deltas to the org's triage inbox (a flat
 * list keyed by `cardId`), then re-ranks and caps it to match the server's
 * canonical list. Pure. Non-triage items are ignored.
 */
export function applyTriageItems(
  list: readonly TriageCardRow[],
  items: CardPushItem[],
): TriageCardRow[] {
  const next = [...list];
  const drop = (cardId: string): void => {
    const i = next.findIndex((r) => r.cardId === cardId);
    if (i >= 0) next.splice(i, 1);
  };
  for (const item of items) {
    if (item.kind === 'triage_upsert') {
      drop(item.card.cardId);
      next.push(item.card);
    } else if (item.kind === 'triage_remove') {
      drop(item.cardId);
    }
  }
  next.sort(compareTriageRows);
  return next.slice(0, TRIAGE_ROW_CAP);
}
